import {
  Alert,
  AppBar,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import {CloseDialogIcon} from "assets/styles/icons";
import {getLoanTypeValue, useProgress} from "context/ProgressProvider";
import {Formik} from "formik";
import {useState} from "react";
import OTPInput from "react-otp-input";
import {useDispatch, useSelector} from "react-redux";
import {useLocation, useNavigate} from "react-router-dom";
import {AuthenticationService} from "services/AuthenticationService";
import {LoanService} from "services/LoanService";
import {
  AppFormikTextField,
  AppPrimaryButton,
  AppSecondaryButton,
} from "shared/components/AppComponents";
import {LoanType} from "shared/helpers/Constants";
import {LoginSchema} from "shared/helpers/Schema";
import Utils from "shared/helpers/Utils";
import {AuthActions} from "state/auth/Action";
import {ProfileActions} from "state/profile/Action";
import {profileState} from "state/profile/Reducer";
import {ProgressActions} from "state/progress/Action";
import {rndProgressState, sblProgressState} from "state/progress/Reducer";
import DialogMultipleLoans from "./apply-loan/DialogMultipleLoans";

// Header Component
const Header = () => {
  const {
    handleReviewDetails,
    handleUpdateUserLoan,
    currentProfile,
    handleLoanStatus,
    isBroker,
  } = useProgress();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authInfo = useSelector((state: any) => state.authState);

  const [showLogin, setShowLogin] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [otp, setOTP] = useState("");
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [loadingResend, setLoadingResend] = useState(false);
  const theme = useTheme();
  const location = useLocation();
  const hostname = window.location.hostname;

  const [hasMultipleLoans, setHasMultipleLoans] = useState(false);
  const [showMultipleLoans, setShowMultipleLoans] = useState(null);
  const [loaderMultiLoans, setLoaderMultiLoans] = useState(false);

  const isSblApplyRoute = location.pathname.startsWith("/sbl");
  const isRndApplyRoute = location.pathname.startsWith("/rnd");

  const updateUserProfile = (data) => {
    if (!data) return;
    let LoanType = getLoanTypeValue(data.loanType);

    const updateUserDeails = new Promise((resolve, reject) => {
      if (data.loanType === 0) {
        dispatch(
          ProfileActions.setSblProfile({
            email: data.email,
            isEmailVerified: data.isEmailVerified,
            loadId: data.loadId,
            userId: data.userId,
            firstName: data.firstName,
            lastName: data.lastName,
            phoneNumber: data.phoneNumber.startsWith("+61 ")
              ? data.phoneNumber
              : data.phoneNumber.startsWith("+61")
                ? "+61 " + data.phoneNumber.slice(3)
                : data.phoneNumber,
            isDocumentMappingCompleted: data.isDocumentMappingCompleted,
            identityVerificationStatus: data.identityVerificationStatus,
          }),
        );
        LoanService.getLoanReview(data.loadId)
          .then((response) => {
            handleUpdateUserLoan(response);
            handleReviewDetails(response);
          })
          .catch((error) => {
            Utils.handleErrorResponse(error);
          });
      }

      if (data.loanType === 1) {
        dispatch(
          ProfileActions.setRndProfile({
            email: data.email,
            isEmailVerified: data.isEmailVerified,
            loadId: data.loadId,
            userId: data.userId,
            firstName: data.firstName,
            lastName: data.lastName,
            phoneNumber: data.phoneNumber.startsWith("+61 ")
              ? data.phoneNumber
              : data.phoneNumber.startsWith("+61")
                ? "+61 " + data.phoneNumber.slice(3)
                : data.phoneNumber,
            isDocumentMappingCompleted: data.isDocumentMappingCompleted,
            identityVerificationStatus: data.identityVerificationStatus,
          }),
        );
        LoanService.getRNDLoanReview(data.loadId)
          .then((response) => {
            handleUpdateUserLoan(response);
            handleReviewDetails(response);
          })
          .catch((error) => {
            Utils.handleErrorResponse(error);
          });
      }
      resolve(true);
    });

    updateUserDeails.then(() => {
      setShowMultipleLoans(null);
      setLoaderMultiLoans(false);
      dispatch(ProgressActions.switchLoanType(LoanType));
    });
  };

  const handleCloseLogin = () => {
    setShowOTP(false);
    setShowLogin(false);
    setOTP("");
  };

  const handleLogin = (ev, value) => {
    AuthenticationService.loginUser({
      otp: parseInt(ev, 10),
      loanType: isSblApplyRoute ? 0 : 1,
      ...value,
    })
      .then((response: any) => {
        let data = response.data.data;

        const loginSession = (e = null) =>
          new Promise((resolve, reject) => {
            dispatch(
              AuthActions.setAuthData({
                accessToken: data.accessToken,
                refreshToken: data.refreshToken,
                isUserLoggedIn: true,
              }),
            );

            if (e === "single") {
              dispatch(
                AuthActions.setAuthData({
                  selectedLoan: data.loanId,
                }),
              );
            }

            resolve(true);
          });

        if (hasMultipleLoans) {
          loginSession().then(() => {
            handleLoanStatus(() => {
              setTimeout(() => {
                handleCloseLogin();
                setShowMultipleLoans(data);
                Utils.handleSuccessResponse(response);
              }, 100);
            });
          });

          return;
        }

        loginSession("single").then(() => {
          handleLoanStatus(() => {});
          updateUserProfile({...data, loadId: data.loanId});
          handleCloseLogin();
        });

        Utils.handleSuccessResponse(response);
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setLoadingLogin(false);
      });
  };

  const handleRequestOTP = (ev) => {
    AuthenticationService.requestOTP(ev)
      .then((response: any) => {
        const {otp} = response.data.data;

        if (
          process.env.REACT_APP_ENV === "development" ||
          hostname === "dev-rnd.ezifin.com"
        ) {
          setOTP(response.data.data.otp);
        }

        const hasLoans = new Promise((resolve, reject) => {
          dispatch(
            AuthActions.setAuthData({
              hasMultipleLoans: response.data.data?.hasMultipleLoans,
            }),
          );
          resolve(true);
        });

        hasLoans.then(() => {
          setHasMultipleLoans(response.data.data?.hasMultipleLoans);
          Utils.handleSuccessResponse(response);
          setShowOTP(true);
        });
      })
      .catch((error: any) => {
        console.log(error);
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setLoadingLogin(false);
        setLoadingResend(false);
      });
  };

  const handleLogout = () => {
    const isSblApplyRoute = location.pathname.startsWith("/sbl");

    const resetStorage = new Promise((resolve, reject) => {
      dispatch(AuthActions.resetStore({}));
      resolve(true);
    });

    resetStorage.then(() => {
      if (isBroker) {
        navigate("/broker");
      } else {
        if (isSblApplyRoute) {
          navigate("/sbl");
        } else {
          navigate("/rnd");
        }
      }
    });
  };

  return (
    <>
      {showLogin && (
        <Formik
          enableReinitialize
          validationSchema={LoginSchema}
          validateOnMount
          onSubmit={(formValues, formik) => {
            setLoadingLogin(true);
            if (showOTP) {
              handleLogin(otp, formValues);
              return;
            }
            handleRequestOTP(formValues);
          }}
          initialValues={{
            email: "",
          }}
        >
          {({handleSubmit, errors, values}) => {
            return (
              <form autoComplete="off">
                <Dialog open={showLogin} fullWidth maxWidth="sm">
                  <DialogTitle>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="h2">Login</Typography>
                      <IconButton onClick={() => handleCloseLogin()}>
                        <CloseDialogIcon />
                      </IconButton>
                    </Box>
                  </DialogTitle>

                  <DialogContent
                    style={{justifyItems: "center", display: "inline-grid"}}
                    sx={{
                      ...(showOTP && {
                        [theme.breakpoints.down("md")]: {
                          padding: 0,
                        },
                      }),
                    }}
                  >
                    {showOTP ? (
                      <>
                        <Typography
                          variant="h3"
                          fontWeight={400}
                          width={"100%"}
                          sx={{
                            ...(showOTP && {
                              [theme.breakpoints.down("md")]: {
                                width: "90%",
                              },
                            }),
                          }}
                        >
                          We’ve sent a verification code to your email. Please
                          check your inbox and enter the code here to continue.
                        </Typography>
                        <Box
                          className="py-6 justify-center"
                          sx={{
                            display: "block",
                            justifyItems: "center",
                            textAlign: "center",
                          }}
                        >
                          <OTPInput
                            value={otp}
                            onChange={(ev) => setOTP(ev)}
                            numInputs={6}
                            renderSeparator={<span className="px-2" />}
                            renderInput={(props) => (
                              <input type="number" {...props} />
                            )}
                            inputStyle="otp-input-box"
                            shouldAutoFocus
                            inputType="number"
                            containerStyle="otp-container"
                          />
                        </Box>
                      </>
                    ) : (
                      <>
                        <Typography
                          variant="h3"
                          fontWeight={400}
                          width={"100%"}
                        >
                          Enter your email to receive One Time Password (OTP)
                        </Typography>
                        <AppFormikTextField
                          placeholder="Enter Email"
                          name="email"
                          outerLabel
                          className="ezifin-default-field"
                        />
                      </>
                    )}
                  </DialogContent>

                  <DialogActions sx={{gap: "12px"}}>
                    <Box
                      sx={{
                        width: "100%",
                        display: !showOTP ? "grid" : "flex",
                        gap: !showOTP ? "" : "12px",
                        flexDirection: !showOTP ? "" : "row-reverse",
                      }}
                    >
                      <AppPrimaryButton
                        title={`${!showOTP ? "Send OTP (For applicant portal)" : "Log in"} `}
                        className="btn-small"
                        onClick={handleSubmit}
                        fullWidth
                        disabled={loadingLogin}
                        isLoading={loadingLogin}
                        loadingText="Please wait.."
                      />

                      {!showOTP && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            my: 2,
                          }}
                        >
                          <Divider sx={{flexGrow: 1}} />
                          <Box sx={{px: 2}}>or</Box>
                          <Divider sx={{flexGrow: 1}} />
                        </Box>
                      )}

                      <AppSecondaryButton
                        title={showOTP ? "Resend" : "Log in to broker portal"}
                        className="btn-small"
                        onClick={() => {
                          if (!showOTP) {
                            navigate("/broker");
                            return;
                          }
                          setLoadingResend(true);
                          handleRequestOTP(values);
                        }}
                        fullWidth
                        disabled={loadingLogin || (showOTP && loadingResend)}
                        isLoading={showOTP && loadingResend}
                        loadingText="Please wait.."
                      />
                    </Box>
                  </DialogActions>
                </Dialog>
              </form>
            );
          }}
        </Formik>
      )}

      <AppBar
        position="sticky"
        color="default"
        elevation={0}
        sx={{border: "1px solid #e7e7e7", background: "white"}}
      >
        <Box
          px={3}
          sx={{
            [theme.breakpoints.down("md")]: {
              padding: "0 0.625vw",
            },
          }}
        >
          <Toolbar sx={{display: "flex", justifyContent: "space-between"}}>
            {/* Logo */}
            <img src="/images/app-logo.svg" />
            {/* Desktop Login */}
            {((!authInfo.isUserLoggedIn && isSblApplyRoute) ||
              (!authInfo.isUserLoggedIn && isRndApplyRoute)) && (
              <Box
                sx={{
                  [theme.breakpoints.down("md")]: {
                    textAlign: "right",
                    width: "50%",
                  },
                }}
              >
                <Typography variant="h3" fontWeight={400}>
                  Already have an account?{" "}
                  <a href="#" onClick={() => setShowLogin(true)}>
                    Log in
                  </a>
                </Typography>{" "}
              </Box>
            )}
            {((authInfo.isUserLoggedIn && isSblApplyRoute) ||
              (authInfo.isUserLoggedIn && isRndApplyRoute)) && (
              <>
                <Typography variant="h3" fontWeight={400}>
                  {currentProfile?.firstName && `${currentProfile.firstName},`}{" "}
                  <a href="#" onClick={() => handleLogout()}>
                    Log out
                  </a>
                </Typography>
              </>
            )}
          </Toolbar>
        </Box>
        {currentProfile.showNonEditableMessage && (
          <Alert severity="info">
            We are currently reviewing your loan request. You may not be able to
            update the details at this time. For more information, please
            contact <a href="mailto:support@ezifin.com">support@ezifin.com.</a>
          </Alert>
        )}
      </AppBar>

      <DialogMultipleLoans
        isOpen={showMultipleLoans}
        setIsOpen={setShowMultipleLoans}
        rowLoanStatus={authInfo.rowLoanStatus}
        callback={(e) => {
          setLoaderMultiLoans(true);
          const multiLoans = new Promise((resolve, reject) => {
            dispatch(
              AuthActions.setAuthData({
                selectedLoan: e.id,
                hasMultipleLoans: true,
              }),
            );
            resolve(true);
          });
          multiLoans.then(() => {
            updateUserProfile({
              ...showMultipleLoans,
              loadId: e.id,
              loanType: e.loanActualType,
            });
          });
        }}
        loaderMultiLoans={loaderMultiLoans}
      />
    </>
  );
};

export default Header;
